import { Lead } from 'models/Leads';
import { FeeAmountTypeEnum } from 'models/PropertyFees';
import { QuoteOverrides, Quote, LeadQuoteOverride } from 'models/Quote';
import {
  HasCleaningFeeItem,
  HasFeeItem,
  HasOrderItems,
} from 'models/hasOrderItems/HasOrderItems';
import { isLeadAMainBooking } from 'utils/lead/leadUtils';

export function prepareOrderItems(
  quote: Quote,
  lead: Lead | null,
): HasOrderItems {
  // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  if (!quote) return null;

  // If the lead is a main booking, SD info should be retrieve from the lead and not the quote
  const isMainBooking = lead ? isLeadAMainBooking(lead) : false;

  let otherFees: HasFeeItem[] = (quote.fees?.otherFees ?? []).map((fee) => {
    return {
      feeId: fee.feeId,
      feeUid: fee.feeUid,
      isEditable: fee.isEditable,
      name: fee.name,
      amountType: fee.amountType,
      taxationRate: fee.taxationRate,
      netPrice: fee.netPrice,
      grossAmount: fee.grossPrice,
      type: fee.type,
      taxAmount: fee.taxAmount,
      removed: false,
      enabled: true,
      isOptional: false,
    } as HasFeeItem;
  });
  // ?.filter((fee) => !fee.removed && fee.enabled);

  // Apply overrides by position: i-th override for (name, feeUid) applies to i-th matching base fee
  const overrideNextIndexByKey = new Map<string, number>();

  quote.overrides?.fees?.otherFees?.forEach((overrideFee) => {
    const key = `${overrideFee.name}-${overrideFee.feeUid ?? 'null'}`;
    const startIndex = overrideNextIndexByKey.get(key) ?? 0;
    const feeIndex = otherFees.findIndex(
      (o, idx) =>
        idx >= startIndex &&
        o.name === overrideFee.name &&
        o.feeUid === overrideFee.feeUid,
    );

    if (feeIndex === -1) {
      // An active property-fee override (feeUid set, enabled, not removed) with no matching
      // line in quote.fees.otherFees describes a fee the backend excluded from the quote —
      // e.g. a zero-amount fee suppressed by PMP-16645. It is reinstatement metadata driving
      // the OrderPropertyFees checkboxes, not a line item, so it must not be displayed.
      // Disabled/removed overrides are still pushed so their state round-trips to the backend.
      const isSuppressedPropertyFee =
        !!overrideFee?.feeUid &&
        (overrideFee?.enabled ?? true) &&
        !(overrideFee?.removed ?? false);

      if (isSuppressedPropertyFee) {
        return;
      }

      otherFees.push({
        feeId: overrideFee?.feeId,
        feeUid: overrideFee?.feeUid,
        isEditable: false,
        name: overrideFee?.name,
        amountType: overrideFee?.amountType,
        taxationRate: overrideFee?.taxationRate,
        netPrice: overrideFee?.netPrice,
        grossAmount: overrideFee?.amount,
        removed: overrideFee?.removed ?? false,
        enabled: overrideFee?.enabled ?? true,
        isOptional: overrideFee?.isOptional ?? true,
      } as HasFeeItem);
    } else {
      overrideNextIndexByKey.set(key, feeIndex + 1);
      otherFees = otherFees.map((otherFee, idx) => {
        if (idx !== feeIndex) {
          return otherFee;
        }
        return {
          ...otherFee,
          removed: overrideFee?.removed ?? otherFee.removed,
          enabled: overrideFee?.enabled ?? otherFee.enabled,
          name: overrideFee?.name ?? otherFee.name,
          taxationRate: overrideFee?.taxationRate ?? otherFee.taxationRate,
          netPrice: overrideFee?.netPrice ?? otherFee.netPrice,
          grossAmount: overrideFee?.amount ?? otherFee.grossAmount,
        } as HasFeeItem;
      });
    }
  });

  return {
    force100PercentPaymentAtReservation:
      quote?.overrides?.force100PercentPaymentAtReservation != null
        ? quote?.overrides?.force100PercentPaymentAtReservation
        : lead?.order?.force100PercentPaymentAtReservation,
    includeSecurityDepositInTotal: isMainBooking
      ? lead?.order?.includeSecurityDepositInTotal
      : quote.includeSecurityDepositInTotal,
    securityDeposit: isMainBooking
      ? lead?.order?.securityDeposit
      : quote.securityDeposit,
    currency: quote.currency,
    totalAmount: quote.totalAmount,
    rent: {
      ...quote.rent,
      extraGuestsBaseNetPrice:
        quote?.overrides?.rent?.extraGuestsBaseNetPrice ||
        quote.rent.extraGuestsBaseNetPrice,
      rentBaseNetPrice:
        quote?.overrides?.rent?.rentBaseNetPrice || quote.rent.rentBaseNetPrice,
      taxAmount: quote?.overrides?.rent?.taxAmount || quote.rent.taxAmount,
      taxationRate:
        quote?.overrides?.rent?.taxationRate || quote.rent.taxationRate,
    },
    services: quote.services,
    adjustmentItems: quote.adjustmentItems,
    propertyName: quote.propertyName,
    fees: {
      cleaningFee: {
        ...(quote?.fees?.cleaningFee ?? {}),
        amountType: FeeAmountTypeEnum.AMOUNT,
        grossAmount: quote?.fees?.cleaningFee?.netPrice || 0,
      } as HasCleaningFeeItem,
      otherFees: [...otherFees],
    },
  };
}

export function convertQuoteOverrideToLeadQuoteOverride(
  overrides: QuoteOverrides,
): LeadQuoteOverride {
  return {
    ...overrides,
    rent: {
      ...overrides.rent,
      rentBaseNetPrice: overrides?.rent?.rentBaseNetPrice,
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      rentNetPrice: null,
      extraGuestsBaseNetPrice: overrides?.rent?.extraGuestsBaseNetPrice,
    },
    fees: {
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      cleaningFee: overrides?.fees?.cleaningFee
        ? {
            netPrice: overrides?.fees?.cleaningFee?.netPrice,
            taxationRate: overrides?.fees?.cleaningFee?.taxationRate,
          }
        : null,
      otherFees:
        overrides?.fees?.otherFees?.map((fee) => ({
          ...fee,
        })) || [],
    },
  };
}
